import { Component } from 'react'
import type { ReactNode } from 'react'

interface HoleMapBoundaryProps {
  /** What stands where the map was — the paper plan, from the stage. */
  fallback: ReactNode
  children: ReactNode
}

interface HoleMapBoundaryState {
  failed: boolean
}

/**
 * A map that falls over leaves the hole DRAWN, not blank.
 *
 * OpenLayers is a canvas library running against tiles fetched from a third
 * party, and the ways it can throw at runtime — a browser with no canvas, a
 * plate that answers something unexpected, a version bump — are not ones a
 * test can enumerate. Without a boundary any of them takes the whole round
 * page down: React unmounts the tree to the root, and a member loses their
 * card, their scores and their handicap movement because a picture failed.
 *
 * So the map is the only thing that is lost. The stage hands its own
 * `HolePlan` in as the fallback — the picture the club drew before there was
 * a map, from the same coordinates — and a member sees a plainer hole rather
 * than an error.
 *
 * ## Its own module, deliberately
 *
 * A boundary has to be imported STATICALLY to catch what its children throw,
 * and `HoleMap` is imported lazily precisely so `ol` stays out of the bundle
 * every member downloads. Keeping the two in one file would put the map
 * library back in the club's entry chunk through the boundary's import —
 * which is the whole thing the split exists to prevent.
 *
 * Nothing is logged here: React reports a caught error to the console itself,
 * so a second line would only be the same failure twice.
 */
export class HoleMapBoundary extends Component<HoleMapBoundaryProps, HoleMapBoundaryState> {
  state: HoleMapBoundaryState = { failed: false }

  static getDerivedStateFromError(): HoleMapBoundaryState {
    return { failed: true }
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}
